import { modelRoleNames } from "@tsmono/inspect-common/utils";

import { headlineMetric } from "../../scoring/headline";
import {
  EvalHeader,
  Log,
  LogDepth,
  LogDetails,
  LogHeader,
  LogPreview,
  SampleDerived,
  SampleSummary,
} from "../api/types";

import {
  deriveLogFields,
  deriveSampleFacts,
  deriveSampleFields,
} from "./derive";

const kDepthOrder: Record<LogDepth, number> = {
  listed: 0,
  previewed: 1,
  detailed: 2,
};

/** Depth only ratchets up within a row's lifetime (resets are explicit). */
export const maxDepth = (a: LogDepth, b: LogDepth): LogDepth =>
  kDepthOrder[a] >= kDepthOrder[b] ? a : b;

/** The previewed-tier attribute columns a preview payload contributes. */
export const previewTier = (
  preview: LogPreview
): Partial<Log> & { depth: LogDepth } => ({
  depth: "previewed",
  status: preview.status,
  error: preview.error,
  version: preview.version,
  eval_id: preview.eval_id,
  run_id: preview.run_id,
  task: preview.task,
  task_id: preview.task_id,
  task_version: preview.task_version,
  model: preview.model,
  model_roles: preview.model_roles,
  started_at: preview.started_at,
  completed_at: preview.completed_at,
  primary_metric: preview.primary_metric,
});

/** The detailed-tier attributes: the flat columns re-derived from the header
 *  plus the header itself and the derived listing columns. */
export const detailTier = (
  header: LogHeader
): Partial<Log> & { depth: LogDepth } => ({
  ...previewTier(toLogPreview(header)),
  depth: "detailed",
  header,
  derived: deriveLogFields(header),
});

/** Split a details payload into its stored header form: everything but the
 *  sample summaries, plus the sample facts derived from them. */
export const toLogHeader = (details: LogDetails): LogHeader => {
  const { sampleSummaries, ...header } = details;
  return { ...header, ...deriveSampleFacts(sampleSummaries) };
};

export interface PreparedSampleSummary {
  summary: SampleSummary;
  derived: SampleDerived;
}

/** A details payload normalized once at ingestion: the header split out, its
 *  detailed-tier row patch, and each summary paired with its derived columns.
 *  Both stores (the query cache's pushes and the IndexedDB write) consume
 *  this single computation, so they can't disagree — and `deriveSampleFields`
 *  never runs twice for one payload. */
export interface PreparedLogDetails {
  header: LogHeader;
  patch: Partial<Log> & { depth: LogDepth };
  summaries: PreparedSampleSummary[];
}

export const prepareLogDetails = (details: LogDetails): PreparedLogDetails => {
  const header = toLogHeader(details);
  return {
    header,
    patch: detailTier(header),
    summaries: details.sampleSummaries.map((summary) => ({
      summary,
      derived: deriveSampleFields(summary),
    })),
  };
};

export const toLogPreview = (header: EvalHeader | LogDetails): LogPreview => {
  const model_roles = modelRoleNames(header.eval.model_roles);

  return {
    eval_id: header.eval.eval_id,
    run_id: header.eval.run_id,

    task: header.eval.task,
    task_id: header.eval.task_id,
    task_version: header.eval.task_version,

    version: header.version,
    status: header.status,
    error: header.error,

    model: header.eval.model,
    model_roles,

    started_at: header.stats?.started_at,
    completed_at: header.stats?.completed_at,

    primary_metric: headlineMetric(header.results, header.eval.headline_metric),
  };
};
